import { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { api } from '../App';

export default function FloatingWhatsApp({ settings, salesInfo }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();

  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminPage) return;
    const delay = settings?.floating_wa_delay_seconds || 3;
    const timer = setTimeout(() => setIsVisible(true), delay * 1000);
    return () => clearTimeout(timer);
  }, [settings, isAdminPage]);

  if (isAdminPage || !isVisible || settings?.floating_wa_enabled === false) return null;

  const whatsappNumber = salesInfo?.whatsapp_number || settings?.whatsapp_number || '082319804253';
  const ctaText = settings?.whatsapp_cta_text || 'Dapatkan Penawaran Terbaik';
  
  const handleClick = async () => {
    try {
      await api.post('/whatsapp-tracking', {
        source_page: location.pathname,
        cta_text: ctaText,
        user_agent: navigator.userAgent
      });
    } catch (e) {}

    let formattedNumber = whatsappNumber.replace(/\D/g, '');
    if (formattedNumber.startsWith('0')) formattedNumber = '62' + formattedNumber.substring(1);
    
    const message = encodeURIComponent(`Halo Bayu, saya tertarik dengan mobil BYD. Bisa info lebih lanjut?`);
    window.open(`https://wa.me/${formattedNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="floating-wa" data-testid="floating-whatsapp" role="complementary" aria-label="Chat WhatsApp">
      {isExpanded && (
        <div
          className="mb-4 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden w-72 animate-fadeInUp"
          data-testid="wa-expanded-card"
          role="dialog"
          aria-labelledby="wa-dialog-title"
        >
          <div className="bg-[#25D366] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center" aria-hidden="true">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p id="wa-dialog-title" className="text-white font-medium text-sm">{salesInfo?.name || 'Bayu Akbar Bisono'}</p>
                <p className="text-white/80 text-xs">{salesInfo?.role || 'Sales Consultant'}</p>
              </div>
            </div>
            <button 
              onClick={() => setIsExpanded(false)}
              className="text-white/80 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="wa-close-btn"
              aria-label="Tutup dialog"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>

          <div className="p-4">
            <div className="bg-slate-100 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-700">Halo! Saya siap membantu Anda mendapatkan penawaran terbaik untuk mobil BYD.</p>
            </div>
            
            <button
              onClick={handleClick}
              className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors min-h-[48px]"
              data-testid="wa-chat-btn"
              aria-label="Mulai chat WhatsApp"
            >
              <MessageCircle className="w-5 h-5" aria-hidden="true" />
              <span className="font-medium">Mulai Chat</span>
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => isExpanded ? handleClick() : setIsExpanded(true)}
        className="floating-wa-button group min-h-[48px] animate-fadeInUp"
        data-testid="floating-wa-btn"
        aria-label={isExpanded ? "Chat via WhatsApp" : `Buka chat WhatsApp - ${ctaText}`}
        aria-expanded={isExpanded}
      >
        <MessageCircle className="w-6 h-6" aria-hidden="true" />
        <span className="hidden sm:block text-sm font-medium max-w-[200px] truncate">{ctaText}</span>
      </button>
    </div>
  );
}
